import { Box } from '@mentimeter/ragnar-ui/box';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import type { ScaleLinear } from 'd3-scale';
import { CorrectnessIndicator } from '../components/CorrectnessIndicator';
import {
  CORRECT_ANSWER_ANIMATION_DURATION,
  SVG_INNER_WIDTH_PADDING,
} from './constants';

export const CorrectAnswerBox = ({
  correctAnswer,
  shouldShowCorrectAnswer,
  shouldShow,
  acceptedRange,
  xScale,
}: {
  correctAnswer: number;
  shouldShowCorrectAnswer: boolean;
  shouldShow: boolean;
  xScale: ScaleLinear<number, number>;
  acceptedRange:
    | {
        min: number;
        max: number;
      }
    | undefined;
}) => {
  const t = useTranslations('presenting');
  const ref = useRef<HTMLDivElement>(null);
  const [refWidth, setRefWidth] = useState<number>(0);

  useEffect(() => {
    if (ref.current) {
      setRefWidth(ref.current.clientWidth);
    }
  }, [acceptedRange, correctAnswer]);

  const chartWidth = xScale.range()[1]!;
  const maxX = chartWidth - refWidth;
  const x = Math.max(xScale(correctAnswer) - refWidth / 2, 0);
  const adjustedX = Math.min(maxX, x);

  return (
    <Box
      position="absolute"
      top={0}
      left={`${SVG_INNER_WIDTH_PADDING}px`}
      ref={ref}
      bg="neutral"
      flexDirection="row"
      alignItems="center"
      borderRadius="2xl"
      p="space6"
      style={{
        opacity: shouldShow && refWidth > 0 ? 1 : 0,
        transition: `opacity ${CORRECT_ANSWER_ANIMATION_DURATION}ms ease-in-out`,
        transform: `translate(${adjustedX}px, 0)`,
      }}
    >
      <CorrectnessIndicator
        shouldShow={shouldShowCorrectAnswer}
        isCorrect={true}
        immediate={false}
        size="40px"
        mr="space4"
      />
      <Text fontSize="200" lineHeight="100">
        {correctAnswer}
      </Text>

      {acceptedRange && (
        <Box ml="space8" alignItems="center" justifyContent="center">
          <Text fontSize="125" color="textWeaker">
            {t('guess_the_number_slide_accepted_range_label')}
          </Text>
          <Text fontSize="175" color="textWeaker">
            {acceptedRange.min}-{acceptedRange.max}
          </Text>
        </Box>
      )}
    </Box>
  );
};
